import React from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';
import { GridEventListener, GridRowParams } from '@mui/x-data-grid';

import { useParams, useLocation } from 'react-router-dom';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';

import routes from 'core/routes';
import { useStore } from 'utils/hooks/store';

import Grid from 'components/Grid';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { getEntityTranslation } from 'stores/EntityDetails/utils';
import { EntityModules, isRegisterEntityModule } from 'core/constants';

import DataError from 'containers/Error/DataError';

import { getBasicColumnsBasedOnEntity } from './utils';
import { getRelatedEntityDetails } from '../helper';

const RelatedEntities: React.FunctionComponent = () => {
  const { routing } = useStore();
  const navBarHeight = useNavBarHeight();

  const params = useParams<{ entityId: string }>();
  const location = useLocation();
  const entityId = params.entityId as string;

  const relatedTo = location.pathname.split('/')[3];

  // `getRelatedEntityDetails` calls `useStore`, so it and the effect are hooks and must precede the
  // guard below; the fetch is skipped inside the effect so an unsupported module issues no request.
  const {
    store: { loadDetails, details, path, detailsPath },
    translations: { plural: entityPlural },
    nameKey,
    entityType,
  } = getRelatedEntityDetails(location.pathname);

  React.useEffect(() => {
    if (!isRegisterEntityModule(relatedTo)) {
      return;
    }

    const fetchDetails = async (): Promise<void> => {
      loadDetails(entityId);
    };

    fetchDetails();
  }, [entityId, relatedTo]);

  // Audits are excluded too — they have no `details` route to reverse into.
  if (!isRegisterEntityModule(relatedTo)) {
    return <DataError />;
  }

  const onRowClick: GridEventListener<'rowClick'> = (entity: GridRowParams): void => {
    routing.push(reverse(routes.dashboard[relatedTo].details, { entityId: entity.row.uuid }));
  };

  const relatedToTranslations = getEntityTranslation[relatedTo];

  const isAudit = entityType === EntityModules.Audits;

  return (
    <Record
      Header={
        <MainHeader
          title={`Related ${relatedToTranslations.plural} to ${details?.entity[nameKey].value || ''}`}
          breadcrumbs={[
            { label: entityPlural, link: path },
            { label: 'Details', link: detailsPath(entityId) },
            {
              label: `Related ${relatedToTranslations.plural.toLowerCase()}`,
              link: detailsPath(entityId),
            },
          ]}
        />
      }
      Content={(onError) => (
        <Grid
          onError={onError}
          disableSelectAllColumns
          onRowClick={onRowClick}
          entity={relatedToTranslations.plural}
          fieldsURL={`/api/entities/${relatedTo}/fields/`}
          basicColumns={getBasicColumnsBasedOnEntity(relatedTo)}
          showQuickFilter={false}
          disableColumnFilter
          disableColumnSorting
          disableSavePreferences
          onlyBasicExport
          dataURL={`/api/entities/${entityType}/${entityId}/${relatedTo}/`}
          customNoRowsText={
            isAudit
              ? `No ${relatedToTranslations.key} related to this audit`
              : `No ${relatedToTranslations.key} have been created yet`
          }
        />
      )}
      navBarHeight={navBarHeight}
    />
  );
};

export default observer(RelatedEntities);
